import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Nav } from './Nav';
import { Footer } from './Footer';
import { HelpFooter } from './HelpFooter';
import { WatermarkOverlay } from './WatermarkOverlay';
import { FreshnessBanner } from './FreshnessBanner';
import { DemoDataBanner } from './DemoDataBanner';
import { CommandPalette } from './CommandPalette';
import { SlicersRail } from './SlicersRail';
import { useEffect } from 'react';
import { useCommandPalette } from '@state/useCommandPalette';
import { useKeyboardShortcuts } from '@state/useKeyboardShortcuts';
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { useSyncSelectedFromUrl } from '@state/programScoped';
import { useDownloadToken } from '@state/useDownloadToken';

/**
 * App shell — editorial paper aesthetic.
 * Per Portal_Frontend_v2.1.1_Spec.md §3 + Claude Design "Data Portal" bundle:
 *  - Topbar (dark ink-950, 56px)
 *  - FreshnessBanner (30px, color-coded by state)
 *  - Body: paper-2 sidebar (260px) + paper main content
 *  - Editorial footer (28px, mono uppercase)
 *  - Watermark overlay (40% opacity, rotated -30deg, fixed position)
 *  - Cmd+K command palette (modal scrim)
 */
export function AppShell() {
  const { isOpen, close } = useCommandPalette();
  const location = useLocation();
  useKeyboardShortcuts();
  // Re-render this subtree (every asset-bearing page) when the download token
  // warms after a cold load or rotates on the warm interval, so assetUrl()-built
  // image/audio/download URLs never paint token-less or serve an expired ?pt=.
  useDownloadToken();
  const hydratePortal = useSetupCampaigns((s) => s.hydrateFromPortal);
  const loadOverrides = useSetupCampaigns((s) => s.loadOverrides);
  // #24 — Surface portal-hydration failures so a non-200 from /api/setup/campaigns
  // or a network error doesn't silently leave the page running on fixtures.
  const hydrationError = useSetupCampaigns((s) => s.hydrationError);
  const clearHydrationError = useSetupCampaigns((s) => s.clearHydrationError);
  useEffect(() => {
    void (async () => {
      await hydratePortal();
      await loadOverrides();
    })();
  }, [hydratePortal, loadOverrides]);
  // Keep the selected-campaign focus in sync with the URL's :program slug.
  // Direct nav / back-button / chip-click-then-navigate all flow through here.
  useSyncSelectedFromUrl();
  // Slicers don't apply on Setup (campaign config reflects onboarding, no filters to slice).
  // Routes are /:program/setup (e.g., /fobt/setup), so endsWith('/setup') is correct;
  // a literal startsWith('/setup') misses the typical program-scoped form.
  const railDisabled = location.pathname.endsWith('/setup');
  const railDisabledReason = railDisabled
    ? 'Campaign Setup reflects onboarding selections — there is nothing to slice. Use the bands below to rename or activate campaigns.'
    : undefined;
  // Pages that bring their own data-bound slicer panel hide the generic rail.
  const railHidden =
    location.pathname.endsWith('/population') ||
    location.pathname.endsWith('/document-library') ||
    location.pathname.endsWith('/distribution') ||
    location.pathname.endsWith('/predictive') ||
    location.pathname.endsWith('/clinical') ||
    location.pathname.endsWith('/ad-hoc-dispatch') ||
    location.pathname.endsWith('/flight-deck') ||
    location.pathname.endsWith('/add-ons') ||
    location.pathname.endsWith('/clarilens') ||
    location.pathname.endsWith('/sentiment') ||
    location.pathname.endsWith('/reports') ||
    location.pathname.endsWith('/operations/member-lookup');

  return (
    <div className="app-root" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--paper)' }}>
      <div data-print-hide>
        <Header />
        <FreshnessBanner />
        <DemoDataBanner />
      </div>
      {hydrationError && (
        <div
          role="alert"
          data-print-hide
          style={{
            background: 'rgba(184, 88, 11, 0.08)',
            color: 'rgb(184, 88, 11)',
            borderBottom: '1px solid rgba(184, 88, 11, 0.25)',
            padding: '6px 14px',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span>
            <strong style={{ marginRight: 6 }}>Portal hydration error:</strong>
            {hydrationError}
          </span>
          <button
            type="button"
            onClick={clearHydrationError}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              padding: '2px 6px',
            }}
          >
            ×
          </button>
        </div>
      )}
      <div className="app-body" style={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <div data-print-hide style={{ display: 'contents' }}>
          <Nav />
        </div>
        <main
          id="main-content"
          tabIndex={-1}
          className="app-main"
          style={{ flex: '1 1 auto', minWidth: 0, overflowX: 'auto', background: 'var(--paper)' }}
        >
          <Outlet />
        </main>
        {!railHidden && (
          <div data-print-hide style={{ display: 'contents' }}>
            <SlicersRail disabled={railDisabled} disabledReason={railDisabledReason} />
          </div>
        )}
      </div>
      <div data-print-hide>
        <HelpFooter />
        <Footer />
      </div>
      <WatermarkOverlay />
      <CommandPalette open={isOpen} onClose={close} />
    </div>
  );
}
